import { useEffect, useState } from 'react';
import { Radio } from 'lucide-react';
import { useFeedStore } from '@/stores/feedStore';
import { useFeedFilterStore } from '@/stores/feedFilterStore';
import { ConnectionStatus } from './ConnectionStatus';
import { FeedFilterPopover } from './FeedFilterPopover';
import { FeedEventCard } from './FeedEventCard';
import { EmptyState } from '@/components/shared/EmptyState';
import { api } from '@/lib/api';
import type { SSEEvent, SSEChangeEvent } from '@/types/api';

function urgencyLevel(score: number): string {
  if (score >= 9) return 'critical';
  if (score >= 7) return 'high';
  if (score >= 5) return 'medium';
  return 'low';
}

function getEventUrgency(event: SSEEvent): number {
  if (event.type === 'change_event_processed') return Number(event.payload.urgencyScore) || 0;
  if (event.type === 'draft_generated') return Number(event.payload.urgencyScore) || 0;
  return 0;
}

function getEventFrameworks(event: SSEEvent): string[] {
  if (event.type === 'change_event_processed') return [...event.payload.frameworksAffected];
  return [];
}

interface LiveRegulatoryFeedProps {
  readonly selectedEventId?: string | null;
  readonly onSelectEvent?: (eventId: string | null) => void;
}

export function LiveRegulatoryFeed({ selectedEventId: _selectedEventId, onSelectEvent }: LiveRegulatoryFeedProps = {}) {
  const { events: liveEvents } = useFeedStore();
  const { frameworks, urgencyLevels } = useFeedFilterStore();
  const [historicalEvents, setHistoricalEvents] = useState<SSEChangeEvent[]>([]);

  // Fetch recent change events on mount to pre-populate the feed
  useEffect(() => {
    api.changeEvents
      .list({ limit: 20 })
      .then((res) => {
        const items = (res as unknown as { events: SSEChangeEvent['payload'][] }).events ?? [];
        const mapped: SSEChangeEvent[] = items.map((ev) => ({
          type: 'change_event_processed' as const,
          payload: ev,
          timestamp: ev.retrievedTimestamp,
          _historical: true,
        } as SSEChangeEvent));
        setHistoricalEvents(mapped);
      })
      .catch(() => {
        // Silently fail — SSE will still work
      });
  }, []);

  // Live SSE events on top, historical below (deduplicated by eventId)
  const liveIds = new Set(
    liveEvents
      .filter((e): e is SSEChangeEvent => e.type === 'change_event_processed')
      .map((e) => e.payload.eventId),
  );
  const deduped = historicalEvents.filter(
    (e) => !liveIds.has(e.payload.eventId),
  );
  const allEvents: SSEEvent[] = [...liveEvents, ...deduped];

  const filtered = allEvents.filter((event) => {
    if (frameworks.length > 0) {
      const eventFrameworks = getEventFrameworks(event);
      if (!eventFrameworks.some((fw) => frameworks.includes(fw))) return false;
    }

    if (urgencyLevels.length > 0) {
      const score = getEventUrgency(event);
      if (!urgencyLevels.includes(urgencyLevel(score))) return false;
    }

    return true;
  });

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between px-1 pb-4">
        <ConnectionStatus />
        <FeedFilterPopover />
      </div>

      <div className="flex-1 space-y-3 overflow-y-auto pr-1">
        {filtered.length === 0 ? (
          <EmptyState
            icon={Radio}
            title="No events yet"
            description="Regulatory events will appear here in real time as they are processed."
          />
        ) : (
          filtered.map((event, index) => (
            <FeedEventCard
              key={`${event.type}-${event.timestamp}-${index}`}
              event={event}
              isHistorical={'_historical' in event}
              onClick={
                event.type === 'change_event_processed' && onSelectEvent
                  ? () => onSelectEvent(event.payload.eventId)
                  : undefined
              }
            />
          ))
        )}
      </div>

    </div>
  );
}
